import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';

import { JobQuoteProps, JobQuoteVariant } from '.';

export interface JobQuoteContextType extends JobQuoteProps {
  tradingName: string;
  quoteReference: string;
  variant: JobQuoteVariant;
}

export const JobQuoteContext = createContext<JobQuoteContextType | null>(null);

export const useJobQuoteContext = () =>
  useContext(JobQuoteContext) as JobQuoteContextType;

export interface JobQuoteProviderProps extends JobQuoteProps {
  children: ReactNode;
}

const JobQuoteProvider: FunctionComponent<JobQuoteProviderProps> = function ({
  children,
  variant = 'full',
  footer,
  job,
  jobCompanyInfo,
  isLoading,
}) {
  const tradingName = useMemo(() => {
    if (isLoading) return 'your tradesperson';
    return jobCompanyInfo?.company_name ?? 'your tradesperson';
  }, [isLoading, jobCompanyInfo]);

  const quoteReference = useMemo(() => {
    if (isLoading) return '--';
    return job?.metarefs?.references?.[0] ?? job?.id ?? '--';
  }, [isLoading, job]);

  const values = useMemo(
    () => ({
      variant,
      tradingName,
      quoteReference,
      footer,
      job,
      jobCompanyInfo,
      isLoading,
    }),
    [
      variant,
      footer,
      tradingName,
      quoteReference,
      job,
      jobCompanyInfo,
      isLoading,
    ],
  );

  return (
    <JobQuoteContext.Provider value={values}>
      {children}
    </JobQuoteContext.Provider>
  );
};

export default JobQuoteProvider;
